// Newbie.jsx
/**
 * Newbie (신규 가입자 요약 카드)
 * --------------------------------------------------------------------
 * 목적:
 *  - 선택된 기간(page: daily/weekly/monthly) 기준으로
 *    "신규 가입자 수"를 원형 배지에 애니메이션 카운트로 표시.
 *  - 상세 페이지(/newbie)로 이동하는 버튼 제공.
 *
 * 핵심 포인트:
 *  - IntersectionObserver로 컴포넌트가 뷰포트에 50% 이상 노출될 때만
 *    숫자 애니메이션을 시작 → 스크롤 위치에 따라 지연 실행/한 번만 실행.
 *  - @react-spring/web의 useSpring을 사용해 0 → count로 2초 동안 천천히 증가.
 *  - 데이터 없을 때(Nodata) UX 제공.
 *
 * props:
 *  - page: 'daily' | 'weekly' | 'monthly'
 *  - currentDate: 현재 기준일(문자열 또는 Date 문자열)
 *  - visitDetailValue: { daily: [], weekly: [], monthly: [] } 형태 가정
 *  - t, i18n: i18n 번역 훅에서 전달받은 함수/객체
 *
 */

import React, { useRef, useState, useEffect } from "react";
import { useSpring, animated } from "@react-spring/web";
import { useNavigate } from "react-router-dom";
import Button from "../common/Button";

function Newbie({ page, currentDate, visitDetailValue, t, i18n }) {
  const ref = useRef(null); // 섹션 루트 DOM 참조(관찰 대상)
  const [inView, setInView] = useState(false); // 뷰포트 노출 여부

  useEffect(() => {
    if (!ref.current) return;

    // 50% 이상 노출되면 애니메이션 시작 후 관찰 해제
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // 최초 1회만 동작하게 끊음
        }
      },
      { threshold: 0.5 } // 요소의 50% 이상이 보일 때 트리거
    );

    observer.observe(ref.current);
    return () => observer.disconnect(); // 언마운트 시 정리
  }, []);

  // 현재 페이지의 신규 가입자 배열(없으면 [])
  const dataArray = visitDetailValue[page] || [];
  const count = dataArray.length; // 신규 가입자 수
  const hasData = count > 0;

  // 숫자 애니메이션: inView && hasData일 때 0 → count로 2초 동안 증가
  const { number } = useSpring({
    from: { number: 0 },
    to: { number: inView && hasData ? count : 0 },
    config: { duration: 2000 },
  });
  const animatedCount = number.to((n) => Math.floor(n)); // 정수화

  // 상세 페이지로 이동(/newbie)
  const navigate = useNavigate();
  const navigateRankDetail = () => {
    // 현재 page, currentDate를 라우팅 state로 전달하여 복원/재조회에 사용
    navigate("/newbie", { state: { page, currentDate } });
  };

  // 기간 라벨(i18n) 계산
  const periodLabel =
    page === "daily"
      ? t("newbie.today")
      : page === "weekly"
      ? t("newbie.thisWeek")
      : t("newbie.thisMonth");

  return (
    <div ref={ref} className="block w-full px-[5%] pt-4 pb-16 bg-white">
      {/* 섹션 제목: 기간 라벨 + 고정 문구(키는 bestSellingMenu지만 신규 전용 문구로 번역해 사용 가정) */}
      <div className="font-semibold text-[5.5vw]">
        {periodLabel} {t("newbie.bestSellingMenu")}
      </div>

      {/* 데이터 없을 때 안내 */}
      {!hasData ? (
        <div className="flex flex-col items-center justify-center h-36 mt-8 gap-2">
          <img
            src="/assets/caution_img.png"
            alt="info icon"
            className="mb-[2vw] w-[8vw]"
          />{" "}
          <span className="text-[3.5vw] text-neutral-500">{t("Nodata")}</span>
        </div>
      ) : (
        // 애니메이션 카운트 원형 배지: 중앙 정렬
        <animated.div className="mt-12 mb-4 w-[25vw] h-[25vw] text-[5vw] font-bold text-white bg-orange-400 rounded-full flex items-center justify-center mx-auto shadow-lg">
          {/* animated.span 안의 숫자만 애니메이션되고, 뒤의 '명'은 고정 */}
          <animated.span>{animatedCount}</animated.span>명
        </animated.div>
      )}

      {/* 상세 보기 버튼: 데이터가 있을 때만 노출 */}
      {hasData && (
        <div className="button-div" id="report" onClick={navigateRankDetail}>
          <Button txt={t("bestMenu.details")} color="transparent" />
        </div>
      )}
    </div>
  );
}

export default Newbie;
